import { useMemo, type ReactNode } from "react";
import { EyeOffIcon, PhoneIcon, ThumbsUpIcon } from "lucide-react";

import {
  type SearchReviewCandidate,
  type SheetCandidacyHydration,
} from "../searchV2CandidateResult";
import { SearchV2ShortlistAction } from "./SearchV2ShortlistAction";

import { Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AiSummaryCard } from "@/components/companies/jobs/candidate-sheet-v2/tabs/AiSummaryCard";
import { ReadOnlyCommsPanel } from "@/components/user-sheet/ReadOnlyCommsPanel";
import { UserSheetActivityRail } from "@/components/user-sheet/UserSheetActivityRail";
import { UserSheetBody } from "@/components/user-sheet/UserSheetBody";
import { UserSheetFrame } from "@/components/user-sheet/UserSheetFrame";
import { UserSheetHeader } from "@/components/user-sheet/UserSheetHeader";
import { UserSheetHeaderDetails } from "@/components/user-sheet/UserSheetHeaderDetails";
import { UserSheetProvider } from "@/components/user-sheet/UserSheetProvider";
import { UserSheetTabs } from "@/components/user-sheet/UserSheetTabs";
import { WorkedAtCustomerBanner } from "@/components/user-sheet/WorkedAtCustomerBanner";
import { withRequiredFields } from "@/components/user-sheet/withRequiredFields";
import { RecordsTab } from "@/components/user-sheet/records/RecordsTab";
import { OtherCandidaciesTab } from "@/components/user-sheet/tabs/OtherCandidaciesTab";
import { ProfileTab } from "@/components/user-sheet/tabs/ProfileTab";
import { RecommendedJobsTab } from "@/components/user-sheet/tabs/RecommendedJobsTab";

import { useGetCallHistory } from "@/hooks/queries/useGetCallHistory";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetOtherCandidacies } from "@/hooks/queries/useGetOtherCandidacies";
import { useSearchInterests } from "@/hooks/search/useSearchInterests";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { SECONDS_PER_MINUTE } from "@/lib/constants";
import { storage } from "@/lib/storage";
import { cn } from "@/lib/utils";
import {
  AgenticScoreStatus,
  ClassifiedKeyword,
  ReviewDecision,
  SearchV2WorkExperience,
} from "@/types";

interface Props {
  candidacyId?: string;
  candidate: SearchReviewCandidate;
  // Fetch the candidacy payload (score, rail, comms) inline — for callers
  // opening the sheet without a hydrated search result.
  includeCandidacyHydration?: boolean;
  isAgentActive: boolean;
  isEmbedded?: boolean;
  isInterestedSelected?: boolean;
  isShortlisted: boolean;
  isShortlisting: boolean;
  jobId?: string;
  onClose: () => void;
  onHide?: () => void;
  onInterestedToggle?: (talentId: number) => void;
  onShortlist: (decision: ReviewDecision) => void;
  shortlistedDecision?: ReviewDecision;
}

export const SearchSheet = ({
  candidacyId,
  candidate,
  includeCandidacyHydration = false,
  isAgentActive,
  isEmbedded = false,
  isInterestedSelected = false,
  isShortlisted,
  isShortlisting,
  jobId,
  onClose,
  onHide,
  onInterestedToggle,
  onShortlist,
  shortlistedDecision,
}: Props) => {
  /* Database CRUD Operations */
  const currentJobId = useCurrentJobId();
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const resolvedJobId = jobId || currentJobId;
  // Read
  // Same worker-scoped queries the tabs run, so the tab bar's counts come
  // straight from the shared cache.
  const callHistoryQuery = useGetCallHistory(candidate.userId);
  const sheetDataQuery = useGetCandidacySheetData({
    includeCandidacyHydration,
    jobId: resolvedJobId,
    userId: candidate.userId,
  });
  // Cast: hydration's inferred type collapses to {} — see
  // SheetCandidacyHydration.
  const hydration = (sheetDataQuery.data?.hydration ??
    null) as SheetCandidacyHydration | null;
  const otherCandidaciesQuery = useGetOtherCandidacies({
    candidacyId,
    talentId: candidate.userId,
  });
  // Interest carries the search-scoped agentic rating shown next to the name.
  const { searchInterests } = useSearchInterests(resolvedJobId);
  const searchInterest = searchInterests?.find(
    (interest) => interest.talentId === candidate.talentId,
  );
  const commuteMinutes =
    searchInterest?.commuteDurationSeconds != null
      ? searchInterest.commuteDurationSeconds / SECONDS_PER_MINUTE
      : null;
  const isAgenticScorePending =
    searchInterest?.agenticScoreStatus === AgenticScoreStatus.PENDING;
  const hasAgenticScoreFailed =
    searchInterest?.agenticScoreStatus === AgenticScoreStatus.FAILED;
  // The interest's AI summary lands after the rating; show the card spinning
  // until it does, nothing if it failed or was never requested.
  const isAiSummaryPending =
    searchInterest?.aiSummaryStatus === AgenticScoreStatus.PENDING;
  const aiSummary = searchInterest?.aiSummaryShort ?? null;

  // Highlight exactly what the search matched — nova returns the matched
  // keywords per work experience; uncategorized so they render in the
  // default blue treatment.
  const highlightKeywords = useMemo<ClassifiedKeyword[]>(() => {
    const matchedKeywords = new Set<string>(
      (candidate.workExperiences ?? []).flatMap(
        (workExperience: SearchV2WorkExperience) =>
          workExperience.matchedKeywords ?? [],
      ),
    );
    return [...matchedKeywords].map((keyword) => ({
      category: null,
      color: null,
      keyword,
    }));
  }, [candidate.workExperiences]);

  /* Helpers */
  const fallbackName =
    `${candidate.firstName ?? ""} ${candidate.lastName ?? ""}`.trim();
  const aiCallsCount = callHistoryQuery.data?.aiCalls.length ?? 0;
  const stafferCallsCount = callHistoryQuery.data?.stafferCalls.length ?? 0;
  const hasCallRecords = aiCallsCount > 0 || stafferCallsCount > 0;
  // Mirrors the Records tab: resumes plus both call lists.
  const resumesCount = sheetDataQuery.data
    ? withRequiredFields(sheetDataQuery.data.resumeFiles, ["id", "title"])
        .length
    : 0;
  const recordsCount = resumesCount + aiCallsCount + stafferCallsCount;
  const otherCandidaciesCount = otherCandidaciesQuery.data?.length;

  /* Render */
  const renderActions = () => {
    return (
      <Row className="items-center gap-2">
        {onHide ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className="mr-2 h-9 w-9 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  onClick={onHide}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <EyeOffIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <Text className="text-xs font-normal text-slate-700">Hide</Text>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        {onInterestedToggle ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  className={cn(
                    "h-9 w-9 border-0 p-0 text-blue-700 hover:bg-blue-50 hover:text-blue-800",
                    isInterestedSelected &&
                      "bg-primary-900 text-white hover:bg-primary-800 hover:text-white",
                  )}
                  onClick={() => {
                    onInterestedToggle(candidate.talentId);
                  }}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <ThumbsUpIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <Text className="text-xs font-normal text-slate-700">
                  Interested
                </Text>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        <SearchV2ShortlistAction
          isAgentActive={isAgentActive}
          isShortlisted={isShortlisted}
          isShortlisting={isShortlisting}
          onShortlist={onShortlist}
          primary
          shortlistedDecision={shortlistedDecision}
          size="card"
        />
      </Row>
    );
  };

  const renderContent = () => {
    return (
      <UserSheetBody commsPanel={<ReadOnlyCommsPanel />}>
        <WorkedAtCustomerBanner
          className={isEmbedded ? "mt-3" : undefined}
          isCurrentLaborupPlacement={
            !!(candidate.currentPlacement ?? hydration?.currentPlacement)
          }
        />
        <UserSheetHeader
          actions={isEmbedded ? undefined : renderActions()}
          agenticScore={
            hasAgenticScoreFailed ? undefined : searchInterest?.agenticScore
          }
          agenticScoreJustification={
            hasAgenticScoreFailed
              ? undefined
              : searchInterest?.agenticScoreJustification
          }
          isAgenticScoreLoading={isAgenticScorePending}
          fallbackName={fallbackName}
        />
        <UserSheetTabs
          scrollHeader={
            <UserSheetHeaderDetails
              metricValues={{
                commuteMinutes,
                lastAiCommunication:
                  candidate.lastAiCommunication ??
                  hydration?.lastAiCommunication,
                lastHumanCommunication:
                  candidate.lastHumanCommunication ??
                  hydration?.lastHumanCommunication,
              }}
            />
          }
          stickyOffsetClassName={isEmbedded ? "-top-px" : undefined}
          tabs={[
            {
              content: (
                <ProfileTab
                  highlightKeywords={highlightKeywords}
                  rail={
                    <UserSheetActivityRail
                      pipelines={
                        candidate.activePipelines ??
                        hydration?.activePipelines ??
                        []
                      }
                      recentActivityAt={
                        candidate.recentActivityAt ??
                        hydration?.recentActivityAt
                      }
                      submissions={
                        candidate.submissions ?? hydration?.submissions ?? []
                      }
                    />
                  }
                  headerCard={
                    <AiSummaryCard
                      isLoading={isAiSummaryPending}
                      summary={aiSummary}
                    />
                  }
                />
              ),
              label: "Profile",
              value: "profile",
            },
            {
              content: <RecordsTab />,
              count: recordsCount || undefined,
              indicator: hasCallRecords ? (
                <PhoneIcon
                  className="h-3.5 w-3.5 shrink-0 text-slate-600"
                  strokeWidth={2}
                />
              ) : undefined,
              label: "Records",
              value: "records",
            },
            {
              content: <OtherCandidaciesTab />,
              count: otherCandidaciesCount,
              label: "Other Candidacies",
              value: "other",
            },
            {
              content: <RecommendedJobsTab />,
              label: "Recommended Jobs",
              value: "recommended",
            },
          ]}
        />
      </UserSheetBody>
    );
  };

  const renderFrame = (content: ReactNode) => {
    if (isEmbedded) return content;
    return (
      <UserSheetFrame fallbackName={fallbackName} onClose={onClose}>
        {content}
      </UserSheetFrame>
    );
  };

  const render = () => {
    return (
      <UserSheetProvider
        candidacyId={candidacyId}
        defaultCommunicationsOpen={storage.getSearchSheetCommsOpen()}
        jobId={resolvedJobId}
        onCommunicationsOpenChange={storage.setSearchSheetCommsOpen}
        workerId={candidate.userId}
      >
        {renderFrame(renderContent())}
      </UserSheetProvider>
    );
  };

  return render();
};
